import {Action, createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    createNote,
    deleteNoteFile,
    filterNotes,
    findTagsLikeName,
    getNoteById,
    getNoteFilesById,
    getNotes,
    saveNote
} from "./noteAPI";
import {CustomerLookup} from "../customers/customersSlice";

export interface NoteTag {
    name: string | undefined,
    index: number | undefined
}

export interface CreateNote {
    id: string,
    content: string | undefined,
    tags: NoteTag[],
    archived: boolean,
    subject: string,
    customerIds: string[],
    created: Date | undefined,
    modified: Date | undefined
}

export interface Note {
    id: string,
    content: string | undefined,
    tags: NoteTag[],
    archived: boolean,
    subject: string,
    customers: CustomerLookup[],
    created: Date | undefined,
    lastModified: Date | undefined,
}

export interface NoteFile {
    name: string,
    mimeType: string,
    length: number,
    id: string
}

export interface TagLookup {
    name: string
}

export interface NoteState {
    status: 'idle' | 'loading' | 'failed'
    tagsLookupStatus: 'idle' | 'loading' | 'failed'
    fileUploadStatus: 'idle' | 'loading' | 'failed'
    createNote: CreateNote
    errorMessage: string | undefined
    createdNoteId: string | undefined
    tagLookup: TagLookup[]
    notes: Note[]
    note: Note | undefined
    files: NoteFile[]
    fileUploadErrorMessage: string | undefined
    noteFileToBeDeleted: NoteFile | undefined
    totalNotes: number
}

const initialState: NoteState = {
    status: 'idle',
    createNote: {
        id: '',
        content: '',
        tags: [],
        archived: false,
        subject: '',
        customerIds: [],
        created: undefined,
        modified: undefined
    },
    errorMessage: undefined,
    createdNoteId: undefined,
    tagsLookupStatus: 'idle',
    fileUploadStatus: 'idle',
    tagLookup: [],
    notes: [],
    note: undefined,
    files: [],
    fileUploadErrorMessage: undefined,
    noteFileToBeDeleted: undefined,
    totalNotes: 0
}

export const postNoteAsync = createAsyncThunk(
    'createNote',
    async ({data, apiToken}
               : { data: CreateNote, apiToken: string }) => {
        const response = await createNote(data, apiToken);
        return response.data;
    }
);

export const saveNoteAsync = createAsyncThunk(
    'saveNote',
    async ({data, apiToken}
               : { data: Note, apiToken: string }) => {
        const response = await saveNote(data, apiToken);
        return response.data;
    }
);

export const findTagsLikeNameAsync = createAsyncThunk(
    'tagLookup',
    async (
        {apiToken, name}: { 
            apiToken: string, name: string }) => {
        const response = await findTagsLikeName(
            apiToken, 
            name);
        return response.data;
    }
);
const getSkip = (activePage: number, pageSize: number) => {
    return (activePage - 1) * pageSize;
}

export const getNotesAsync = createAsyncThunk(
    'notes',
    async ({apiToken, activePage, pageSize}: {
        apiToken: string, activePage: number, pageSize: number
    }) => {
        const response = await getNotes(apiToken, getSkip(activePage, pageSize), pageSize);
        return response.data;
    }
);

export const getNoteByIdAsync = createAsyncThunk(
    'note',
    async ({apiToken, id}: { apiToken: string, id: string }) => {
        const response = await getNoteById(apiToken, id);
        return response.data.value[0];
    }
);

export const getNoteFilesByIdAsync = createAsyncThunk(
    'notefiles',
    async ({apiToken, id}: { apiToken: string, id: string }) => {
        const response = await getNoteFilesById(apiToken, id);
        return response.data.value[0];
    }
);

export const deleteNoteFileAsync = createAsyncThunk(
    'notefile',
    async ({apiToken, noteId, noteFileId}: {
        apiToken: string,
        noteId: string,
        noteFileId: string
    }) => {
        await deleteNoteFile(apiToken, noteId, noteFileId);
    });

export const filterNotesAsync = createAsyncThunk(
    'filterNotes',
    async ({apiToken, filter, activePage, pageSize}: {
        apiToken: string,
        filter: string, 
        activePage: number, 
        pageSize: number
    }) => {
        const response = await filterNotes(apiToken, filter, getSkip(activePage, pageSize), pageSize);
        return response.data;
    });

export const noteSlice = createSlice({
    name: 'note',
    initialState,
    reducers: {
        dummy: (_, __: Action) => {
        },
        uploadFile: (noteState: NoteState, action: PayloadAction<string>) => {
            switch (action.payload) {
                case 'preparing':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'loading';
                    break;
                case 'getting_upload_params':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'loading';
                    break;
                case 'uploading':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'loading';
                    break;
                case 'headers_received':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'loading';
                    break;
                case 'done':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'idle';
                    break;
                case 'removed':
                    noteState.fileUploadErrorMessage = undefined;
                    noteState.fileUploadStatus = 'idle';
                    break;
                default:
                    noteState.fileUploadErrorMessage = 'File upload failed';
                    noteState.fileUploadStatus = 'failed';
            }
        },
        markNoteFileAsDeletable: (noteState: NoteState, action: PayloadAction<NoteFile>) => {
            noteState.noteFileToBeDeleted = action.payload;
        },
        unmarkNoteFileAsDeletable: (noteState: NoteState, _) => {
            noteState.noteFileToBeDeleted = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(postNoteAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.createdNoteId = undefined;
                return newState;
            })
            .addCase(postNoteAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.createdNoteId = action.payload;
                return newState;
            })
            .addCase(postNoteAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message;
                newState.createdNoteId = undefined
                return newState;
            })
            .addCase(saveNoteAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                return newState;
            })
            .addCase(saveNoteAsync.fulfilled, (state, _) => {
                const newState = {...state};
                newState.status = 'idle';
                return newState;
            })
            .addCase(saveNoteAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed'
                newState.errorMessage = action.error.message;
                return newState;
            })
            .addCase(findTagsLikeNameAsync.pending, (state, _) => {
                const newState = {...state};
                newState.tagsLookupStatus = 'loading';
                newState.tagLookup = [];
                return newState;
            })
            .addCase(findTagsLikeNameAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.tagsLookupStatus = 'idle';
                newState.tagLookup = action.payload;
                return newState;
            })
            .addCase(findTagsLikeNameAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.tagsLookupStatus = 'failed';
                newState.errorMessage = action.error.message;
                newState.tagLookup = []
                return newState;
            })
            .addCase(getNotesAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.notes = []
                return newState;
            })
            .addCase(getNotesAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.notes = action.payload.value;
                newState.totalNotes = action.payload['@odata.count'];
                return newState;
            })
            .addCase(getNotesAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message
                newState.notes = [];
                return newState;
            })
            .addCase(getNoteByIdAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.note = undefined;
                return newState;
            })
            .addCase(getNoteByIdAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.note = action.payload;
                return newState;
            })
            .addCase(getNoteByIdAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.note = undefined;
                newState.errorMessage = action.error.message;
                return newState;
            })
            .addCase(getNoteFilesByIdAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.files = [];
                return newState;
            })
            .addCase(getNoteFilesByIdAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.files = action.payload.files;
                return newState;
            })
            .addCase(getNoteFilesByIdAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.files = [];
                newState.errorMessage = action.error.message;
                return newState;
            })
            .addCase(deleteNoteFileAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.noteFileToBeDeleted = undefined;
                return newState;
            })
            .addCase(deleteNoteFileAsync.fulfilled, (state) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.noteFileToBeDeleted = undefined;
                return newState;
            })
            .addCase(deleteNoteFileAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.noteFileToBeDeleted = undefined;
                newState.errorMessage = action.error.message
                return newState;
            })
            .addCase(filterNotesAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.notes = [];
                return newState;
            })
            .addCase(filterNotesAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.notes = action.payload.value;
                newState.totalNotes = action.payload['@odata.count'];
                return newState;
            })
            .addCase(filterNotesAsync.rejected, (state) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.notes = [];
                return newState;
            })
    }
});

export const {uploadFile} = noteSlice.actions;
export const {markNoteFileAsDeletable} = noteSlice.actions;
export const {unmarkNoteFileAsDeletable} = noteSlice.actions;
export default noteSlice.reducer;
